import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';
import { PlayIcon } from 'lucide-react';

import { EmptyValue } from '@/components/atoms/EmptyValue/EmptyValue';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SortableHeader } from '@/components/ui/table/sortable-header';
import type { EvaluationWithSimulation } from '@/modules/simulations/types';
import {
  EVALUATION_STATUS_BADGE_FLAVOUR,
  EVALUATION_STATUS_TRANSLATION_KEYS,
  CHANNEL_LABEL_KEYS,
} from '@/modules/simulations/types';
import { CHANNEL_ICONS, formatDurationSeconds } from '@/modules/simulations/utils';
import { COLUMN_PRESETS } from '@/util/constants';

const TOOLTIP_REASONING_MAX_CHARS = 200;

interface ColumnsProps {
  onPreview: (evaluation: EvaluationWithSimulation) => void;
}

export const columns = ({ onPreview }: ColumnsProps): ColumnDef<EvaluationWithSimulation>[] => [
  {
    id: 'preview',
    enableHiding: false,
    size: 60,
    minSize: 48,
    maxSize: 72,
    meta: { className: 'text-center' },
    header: () => null,
    cell: ({ row }) => (
      <InfoTooltip content={t('simulations.preview')} side="top">
        <Button
          variant="secondary-neutral"
          size="icon-lg"
          type="button"
          aria-label={t('simulations.preview')}
          onClick={(e) => {
            // preventDefault so the click doesn't trigger the row's wrapping <Link> navigation
            e.preventDefault();
            e.stopPropagation();
            onPreview(row.original);
          }}
        >
          <PlayIcon size={16} />
        </Button>
      </InfoTooltip>
    ),
  },
  {
    id: 'name',
    accessorFn: (row) => row.simulation.name,
    enableHiding: false,
    ...COLUMN_PRESETS.FLEX,
    meta: { preset: 'FLEX' },
    header: ({ column }) => (
      <SortableHeader column={column}>{t('simulations.name')}</SortableHeader>
    ),
    cell: ({ row }) => <span className="truncate font-medium">{row.original.simulation.name}</span>,
  },
  {
    id: 'result',
    accessorKey: 'status',
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD', label: 'simulations.result' },
    header: ({ column }) => (
      <SortableHeader column={column}>{t('simulations.result')}</SortableHeader>
    ),
    cell: ({ row }) => {
      const { status, evaluator_reasoning, error_message } = row.original;
      const flavour = EVALUATION_STATUS_BADGE_FLAVOUR[status];
      const badge = (
        <Badge type="secondary" flavour={flavour} rounded={false}>
          {t(EVALUATION_STATUS_TRANSLATION_KEYS[status] ?? status)}
        </Badge>
      );
      const tooltipContent = error_message ?? evaluator_reasoning;
      if (tooltipContent) {
        const truncated =
          tooltipContent.length > TOOLTIP_REASONING_MAX_CHARS
            ? `${tooltipContent.slice(0, TOOLTIP_REASONING_MAX_CHARS).trimEnd()}…`
            : tooltipContent;
        return (
          <InfoTooltip content={truncated} size="large">
            <span>{badge}</span>
          </InfoTooltip>
        );
      }
      return badge;
    },
  },
  {
    id: 'turns',
    accessorKey: 'turn_count',
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD', label: 'simulations.turns' },
    header: ({ column }) => (
      <SortableHeader column={column}>{t('simulations.turns')}</SortableHeader>
    ),
    cell: ({ row }) => {
      const { turn_count } = row.original;
      if (turn_count === null || turn_count === undefined) {
        return <EmptyValue />;
      }
      return <span>{turn_count}</span>;
    },
  },
  {
    id: 'channel',
    accessorFn: (row) => row.simulation.channel_type,
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD', label: 'simulations.channel' },
    header: ({ column }) => (
      <SortableHeader column={column}>{t('simulations.channel')}</SortableHeader>
    ),
    cell: ({ row }) => {
      const channelType = row.original.simulation.channel_type;
      const Icon = CHANNEL_ICONS[channelType];
      return (
        <div className="flex items-center gap-2">
          <Icon size={16} className="shrink-0 text-foreground-muted" />
          <span>{t(CHANNEL_LABEL_KEYS[channelType])}</span>
        </div>
      );
    },
  },
  {
    id: 'category',
    accessorFn: (row) => row.simulation.category,
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD', label: 'simulations.category' },
    header: ({ column }) => (
      <SortableHeader column={column}>{t('simulations.category')}</SortableHeader>
    ),
    cell: ({ row }) => {
      const { category } = row.original.simulation;
      if (!category) {
        return <EmptyValue />;
      }
      return (
        <Badge type="secondary" flavour="informational" rounded={false} className="max-w-full">
          <span className="truncate">{category}</span>
        </Badge>
      );
    },
  },
  {
    id: 'duration',
    accessorKey: 'duration_seconds',
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD', label: 'simulations.duration' },
    header: ({ column }) => (
      <SortableHeader column={column}>{t('simulations.duration')}</SortableHeader>
    ),
    cell: ({ row }) => {
      const { duration_seconds } = row.original;
      if (duration_seconds === null || duration_seconds === undefined) {
        return <EmptyValue />;
      }
      return <span>{formatDurationSeconds(duration_seconds)}</span>;
    },
  },
];
